/**
 * CampaignStats component - displays campaign email statistics
 */

import type { Campaign } from '../../services/marketingApi';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Mail, CheckCircle, Eye, MousePointer, AlertTriangle } from 'lucide-react';

interface CampaignStatsProps {
  campaign: Campaign;
}

export function CampaignStats({ campaign }: CampaignStatsProps) {
  const stats = campaign.stats || {
    total: campaign.sent_count,
    sent: campaign.sent_count,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
  };

  const calculateRate = (value: number, base: number) => {
    if (base === 0) return '0%';
    return `${Math.round((value / base) * 100)}%`;
  };

  const statCards = [
    {
      label: 'Enviados',
      value: stats.sent,
      icon: Mail,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
    },
    {
      label: 'Entregados',
      value: stats.delivered,
      rate: calculateRate(stats.delivered, stats.sent),
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-950',
    },
    {
      label: 'Abiertos',
      value: stats.opened,
      rate: calculateRate(stats.opened, stats.delivered),
      icon: Eye,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
    },
    {
      label: 'Clics',
      value: stats.clicked,
      rate: calculateRate(stats.clicked, stats.opened),
      icon: MousePointer,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-950',
    },
    {
      label: 'Rebotados',
      value: stats.bounced,
      rate: calculateRate(stats.bounced, stats.sent),
      icon: AlertTriangle,
      color: 'text-red-500',
      bgColor: 'bg-red-50 dark:bg-red-950',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {statCards.map((stat) => (
        <Card key={stat.label}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.label}</CardTitle>
            <div className={`p-2 rounded-full ${stat.bgColor}`}>
              <stat.icon className={`h-4 w-4 ${stat.color}`} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            {stat.rate && (
              <p className="text-xs text-muted-foreground">{stat.rate}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
